import { useCallback, useEffect, useRef, useState } from 'react';
import { dataDictionaryApi } from './dataDictionaryApi';
import { errorMessage } from './dataDictionaryUtils';

/**
 * Loads the slow-changing dictionary metadata used across the page:
 * folder tree, tag list with counts, and per-table stats.
 * `refresh()` reloads all three (call after edits); each piece keeps its last value while reloading.
 */
export default function useDictionaryMeta(enabled) {
  const [folders, setFolders] = useState([]);
  const [noFolder, setNoFolder] = useState({ total: 0, visible: 0, complete: 0 });
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const genRef = useRef(0);

  const refresh = useCallback(async () => {
    const gen = ++genRef.current;
    setLoading(true);
    setError('');
    const results = await Promise.allSettled([
      dataDictionaryApi.folders(),
      dataDictionaryApi.tags(),
      dataDictionaryApi.stats(),
    ]);
    if (gen !== genRef.current) return;
    const [f, t, s] = results;
    if (f.status === 'fulfilled') {
      setFolders(Array.isArray(f.value?.folders) ? f.value.folders : []);
      setNoFolder(f.value?.no_folder || { total: 0, visible: 0, complete: 0 });
    }
    if (t.status === 'fulfilled') setTags(Array.isArray(t.value?.tags) ? t.value.tags : []);
    if (s.status === 'fulfilled') setStats(s.value || null);
    const failed = results.find((r) => r.status === 'rejected');
    if (failed) setError(errorMessage(failed.reason, 'Failed to load dictionary metadata'));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    return () => {
      genRef.current += 1;
    };
  }, [enabled, refresh]);

  return { folders, noFolder, tags, stats, loading, error, refresh };
}
